import React from 'react'
import { format } from 'date-fns'
import OrderNumber from './OrderNumber.js'
import PrintIcon from '../icons/Print.js'
import PrintCateringOrder from './PrintCateringOrder.js'
import CheckIcon from '../icons/Check.js'
import { getPaymentMethod } from '../helpers/getPaymentMethod.js'
import styles from './CateringOrderItem.module.css'
import { Order, PrintedByUserWithType } from '../types/Order.js'
import getReprintStatusOrders from '../helpers/getReprintStatusOrders.js'
import PrintPortal from './PrintPortal.jsx'
import { getPaymentStatusText } from '../helpers/getPaymentStatusText.js'

interface Props {
  item: Order
  userId?: string
  onLineItemClick?: (id: string) => void
  status: string | number
  storeId: string
  onPrintOrder?: (obj: { storeId: string, orderIds: string, userId?: string, type: string }) => void
  updatedOrderModal?: boolean
}

function CateringOrderLineItem({
  item,
  userId,
  onLineItemClick,
  status,
  storeId,
  onPrintOrder,
  updatedOrderModal = false
}: Props) {
  const {
    id,
    deliveryDisplayName,
    deliveryBongDisplayName,
    orderItemsCatering,
    deliveryTimeSlot,
    paymentStatus,
    paymentProviderType,
    orderNo,
    printedByUsersWithType,
    orderHistory
  } = item || {}

  const printedCateringOrder = () => {
    const printed = printedByUsersWithType?.find(print => 
      print.actions?.some(action => action.type === "IncomingOrders" && print.userId === userId)
    )?.actions?.find(action => action.type === "IncomingOrders")
    
    if (printed) {
      return true
    } else {
      return false
    }
  }

  const deliveryDate = format(new Date(item.deliveryDate), 'yyyy-MM-dd')
  const orderNoColor =
    status === 5 ? '#ededed' : status === 'upcoming' ? '#fdc300' : '#94c11a'
  const orderLastChanged = orderHistory && orderHistory[0].date
  const [needsReprinting, setNeedsReprinting] = React.useState(false)
  const [isPrinting, setIsPrinting] = React.useState(false)

  React.useEffect(() => {
    if (userId) { 
      const reprintStatus = getReprintStatusOrders(printedByUsersWithType as PrintedByUserWithType[], userId, orderLastChanged as string) as boolean
      setNeedsReprinting(reprintStatus)
    }
  }, [item, item.printedByUsersWithType, item.orderHistory])

  const paymentInfo = React.useMemo(() => {
    return getPaymentStatusText(paymentStatus, deliveryDate)
  }, [paymentStatus, deliveryDate])

  const paymentMethod = React.useMemo(() => {
    return getPaymentMethod(paymentProviderType)
  }, [paymentProviderType])

  function getArticleText() {
    const numberOfItems = orderItemsCatering?.reduce((prev, current) => {
      return prev + current.amount
    }, 0)
    if (numberOfItems > 1 || numberOfItems === 0) {
      return `${numberOfItems} artiklar`
    }
    return `${numberOfItems} artikel`
  }

  function onOpenDetail(event: React.MouseEvent<HTMLElement, MouseEvent>) {
    const element = event.target as HTMLElement
    if (element.tagName !== 'svg' && element.tagName !== 'path') {
      onLineItemClick && onLineItemClick(id)
    }
  }

  function onPrint() {
    setIsPrinting(true)
    setTimeout(() => {
      window.print()
      setTimeout(() => {
        setIsPrinting(false)
      }, 1000)
    }, 100)
  }

  function onPrintStatus() {
    onPrintOrder && onPrintOrder({
      storeId,
      orderIds: id,
      userId,
      type: 'IncomingOrders'
    })
    onPrint()
  }

  return (
    <>
      <li>
        <div 
          className={styles.item}
          onClick={(event) => !updatedOrderModal && onOpenDetail(event)}
          style={{ cursor: updatedOrderModal ? 'default' : 'pointer' }}>
          <div className={styles.text}>
            <div className={styles.orderNo} style={{ paddingRight: !updatedOrderModal ? '2.5rem' : '0' }}>
              <OrderNumber
                color={orderNoColor}
                textColor={status === 5 ? '#bdbbb9' : '#FFFFFF'}
                number={orderNo}
                dynamicWidth={true}
              />
            </div>

            {!updatedOrderModal ? (
              <>
                <div className={styles.articles}>
                  <span>{getArticleText()}</span>
                </div>
                <div className={styles.paymentStatus}>
                  {paymentMethod}
                  {paymentMethod === 'Betala i butik' ? '' : `: ${paymentInfo}`}
                </div>
                <div className={styles.deliveryTime}>
                  {deliveryBongDisplayName
                    ? deliveryBongDisplayName
                    : deliveryDisplayName}
                  {deliveryTimeSlot ? `: ${item.deliveryTimeSlot}` : ''}
                </div>
                <div className={styles.printStatus}>
                  {(printedCateringOrder()) && !needsReprinting ? (
                    <>
                      <div className={styles.printedLabel}>
                        <div className={styles.checkIconBox}>
                          <CheckIcon className={styles.checkIcon} color='#2F6147' />
                        </div>
                        Utskriven
                      </div>
                      <button
                        onClick={(event) => {
                          event.stopPropagation()
                          onPrint()
                        } }
                        className={styles.printedButton}
                      >
                        <div className={styles.printIconBox}>
                          <PrintIcon color='#cf2e05' className={styles.printIcon} />
                        </div>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={onPrintStatus}
                      className={styles.printedButton}
                    >
                      <div className={styles.printIconBox}>
                        <PrintIcon color='#cf2e05' className={styles.printIcon} />
                      </div>
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>Leveransdatum: {deliveryDate}</div>
                <div>Ändrad: {orderHistory && format(new Date(orderHistory[orderHistory.length - 1].date), 'yyyy-MM-dd')}</div>
                <div>Ändrad av: Butik</div>
              </>
            )}
          </div>
        </div>
      </li>

      {isPrinting && (
        <PrintPortal>
          <div className="printView">
            <PrintCateringOrder order={item} />
          </div>
        </PrintPortal>
      )}
    </>
  )
}

export default CateringOrderLineItem
